import { __decorate } from "tslib";
import { html, LitElement, property } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';
import { getBoolConverter, registerElement } from 'smarttbot-utils';
import styles, { dynamicStyles } from './styles';
const ELLIPSIS = '...';
const MAXIMUM_NUMBER_OF_PAGES = 7;
export default class Pagination extends LitElement {
    constructor() {
        super(...arguments);
        this.color = 'primary';
        this.totalPages = 1;
        this.currentPage = 1;
        this.getCurrentPage = () => console.error('No function has been passed to "getCurrentPage" property.');
        this.disabled = false;
        this.hasNavigationButtons = true;
    }
    connectedCallback() {
        super.connectedCallback();
        if (isNaN(this.currentPage) || isNaN(this.totalPages)) {
            this.currentPage = 1;
            this.totalPages = 1;
            this.getCurrentPage(this.currentPage);
        }
        if (this.currentPage > this.totalPages || this.currentPage <= 0) {
            this.currentPage = 1;
            this.getCurrentPage(this.currentPage);
        }
    }
    _pagesRemaining(totalPagesList) {
        const pagesRemainingLeft = totalPagesList.filter((page) => page < this.currentPage).length;
        const pagesRemainingRight = totalPagesList.filter((page) => page > this.currentPage).length;
        return [pagesRemainingLeft, pagesRemainingRight];
    }
    _createCurrentPagesAvailableWithEllipsis(currentPagesAvaiable, hasTooManyPages, isPagesRemainingLeftGreaterThanThree, isPagesRemainingRightGreaterThanThree) {
        const currentPagesAvailableWithEllipsis = [
            ...currentPagesAvaiable
        ];
        if (hasTooManyPages && isPagesRemainingRightGreaterThanThree)
            currentPagesAvailableWithEllipsis.splice(currentPagesAvaiable.length - 1, 0, ELLIPSIS);
        if (hasTooManyPages && isPagesRemainingLeftGreaterThanThree)
            currentPagesAvailableWithEllipsis.splice(1, 0, ELLIPSIS);
        return currentPagesAvailableWithEllipsis;
    }
    _createCurrentPagesList() {
        const totalPagesList = Array.from({ length: this.totalPages }, (_, i) => i + 1);
        const [pagesRemainingLeft, pagesRemainingRight] = this._pagesRemaining(totalPagesList);
        const isPagesRemainingRightGreaterThanThree = pagesRemainingRight > 3;
        const isPagesRemainingLeftGreaterThanThree = pagesRemainingLeft > 3;
        const hasTooManyPages = this.totalPages > MAXIMUM_NUMBER_OF_PAGES;
        const currentPagesAvaiable = totalPagesList.filter((page) => !hasTooManyPages ||
            page === 1 ||
            page === this.totalPages ||
            page === this.currentPage - 1 ||
            page === this.currentPage ||
            page === this.currentPage + 1 ||
            (page > this.totalPages - 5 &&
                !isPagesRemainingRightGreaterThanThree) ||
            (page <= 5 && !isPagesRemainingLeftGreaterThanThree));
        return this._createCurrentPagesAvailableWithEllipsis(currentPagesAvaiable, hasTooManyPages, isPagesRemainingLeftGreaterThanThree, isPagesRemainingRightGreaterThanThree);
    }
    _createPaginationButtons() {
        return this._createCurrentPagesList().map((pageNumber) => {
            const isCurrentPage = this.currentPage === pageNumber;
            if (pageNumber === ELLIPSIS)
                return html ` <div
          class=${classMap({
                    'button-base': true,
                    ellipsis: true,
                    'ellipsis-disabled': this.disabled,
                    'no-hover': true
                })}
        >
          ${pageNumber}
        </div>`;
            return html ` <button
        tabindex="0"
        aria-label=${`Botão da página ${pageNumber}`}
        name=${pageNumber}
        class=${classMap({
                'button-base': true,
                selected: isCurrentPage,
                'no-hover': this.disabled
            })}
        @click=${this._handlePageClick}
        ?disabled=${this.disabled}
      >
        ${pageNumber}
      </button>`;
        });
    }
    _createNavigationButton(direction) {
        if (!this.hasNavigationButtons)
            return;
        let isDisabled = this.disabled || this.currentPage === 1;
        let handleClick = this._handlePreviousClick;
        const isNextButton = direction === 'next';
        if (isNextButton) {
            isDisabled = this.disabled || this.currentPage >= this.totalPages;
            handleClick = this._handleNextClick;
        }
        return html `
    <button
      tabindex="0"
      aria-label=${`Botão para ${isNextButton ? 'avançar' : 'voltar'} uma página`}
      class=${classMap({
            'button-base': true,
            'no-hover': this.disabled || isDisabled
        })}
      @click=${handleClick}
      ?disabled=${isDisabled}
     />
      <span class=${classMap({
            arrow: true,
            left: !isNextButton,
            right: isNextButton,
            'arrow-disabled': isDisabled
        })} />
     </button>`;
    }
    _handlePageClick({ target: { name } }) {
        this.currentPage = Number(name);
        this.getCurrentPage(this.currentPage);
    }
    _handlePreviousClick() {
        this.currentPage -= 1;
        this.getCurrentPage(this.currentPage);
    }
    _handleNextClick() {
        this.currentPage += 1;
        this.getCurrentPage(this.currentPage);
    }
    render() {
        return html `
      <style>
        ${dynamicStyles(this.color)}
      </style>
      <div class="pagination-container">
        ${this._createNavigationButton('previous')}
        ${this._createPaginationButtons()}
        ${this._createNavigationButton('next')}
      </div>
    `;
    }
}
Pagination.styles = styles;
__decorate([
    property({ type: String })
], Pagination.prototype, "color", void 0);
__decorate([
    property({ type: Number })
], Pagination.prototype, "totalPages", void 0);
__decorate([
    property({ type: Number })
], Pagination.prototype, "currentPage", void 0);
__decorate([
    property({ attribute: false, reflect: true })
], Pagination.prototype, "getCurrentPage", void 0);
__decorate([
    property({ type: Boolean, converter: getBoolConverter })
], Pagination.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, converter: getBoolConverter })
], Pagination.prototype, "hasNavigationButtons", void 0);
registerElement('sb-pagination', Pagination);
